/**
 * Typed Document Intake client (PROD-3204, DF-01), one function per route of the D1 contract v0.
 *
 * All routes are Scrips.Patient routes under `api/Patients/{patientId}/Intake/...`, on the same host and prefix as the
 * Patient lab routes. Every call carries the user's own token (`api-client`); the server takes `confirmedBy`,
 * `editedBy` and the like from it, never from the body.
 *
 * Errors are ProblemDetails with a stable snake_case `code`; they surface as `IntakeApiError`.
 */
import { ApiError, fetchWithAuth, get, post } from '@/shared/api/api-client';
import type {
  ChangePatientBody,
  ChangePatientResult,
  ConfirmBody,
  ConfirmResult,
  DocumentDecisionBody,
  InboxAddResult,
  InboxDisregardReason,
  InboxDisregardResult,
  InboxDocument,
  IntakeBatch,
  IntakeDocType,
  IntakeReview,
  IntakeRunStatus,
  LabComponentSearch,
  ManualRowBody,
  ReviewRow,
  RowDisregardReason,
  RowEditBody,
  Versioned,
} from '../types';

/** A failed intake call: HTTP status, the ProblemDetails `code` (null when the body had none) and any `rowIds`. */
export class IntakeApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly rowIds: string[];
  readonly title: string | null;

  constructor(status: number, code: string | null, title: string | null, rowIds: string[] = []) {
    super(`Intake API ${status}${code ? ` ${code}` : ''}`);
    this.name = 'IntakeApiError';
    this.status = status;
    this.code = code;
    this.title = title;
    this.rowIds = rowIds;
  }
}

/** Reads a ProblemDetails body. A body that is not JSON keeps the status and has no code. */
export function toIntakeError(status: number, body: string): IntakeApiError {
  try {
    const p = JSON.parse(body) as { code?: unknown; title?: unknown; rowIds?: unknown };
    return new IntakeApiError(
      status,
      typeof p.code === 'string' ? p.code : null,
      typeof p.title === 'string' ? p.title : null,
      Array.isArray(p.rowIds) ? p.rowIds.filter((x): x is string => typeof x === 'string') : [],
    );
  } catch {
    return new IntakeApiError(status, null, null);
  }
}

/** Maps the shared client's `ApiError` to `IntakeApiError`; anything else (network, abort) is rethrown as is. */
async function call<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof ApiError) throw toIntakeError(e.status, e.body);
    throw e;
  }
}

const seg = encodeURIComponent;

export const intakeBase = (patientId: string) => `/api/Patients/${seg(patientId)}/Intake`;
const runBase = (patientId: string, runId: string) => `${intakeBase(patientId)}/Runs/${seg(runId)}`;

/**
 * The browser could not read the run ETag: the server did not expose it over CORS (`Access-Control-Expose-Headers`).
 * A change is then never sent without `If-Match` (it would only come back 428), and the screens do not reload on it.
 */
export const ETAG_UNREADABLE = 'etag_unreadable';

/** A JSON request whose reply carries the run ETag. Every change (not GET) needs `ifMatch`, or nothing is sent. */
async function versioned<T>(
  endpoint: string,
  init: { method: string; body?: unknown; ifMatch?: string | null; idempotencyKey?: string },
  signal?: AbortSignal,
): Promise<Versioned<T>> {
  const headers: Record<string, string> = {};
  if (init.method !== 'GET' && !init.ifMatch) throw new IntakeApiError(0, ETAG_UNREADABLE, null);
  if (init.ifMatch) headers['If-Match'] = init.ifMatch;
  if (init.idempotencyKey) headers['Idempotency-Key'] = init.idempotencyKey;
  const res = await fetchWithAuth(
    endpoint,
    { method: init.method, headers, body: init.body === undefined ? undefined : JSON.stringify(init.body) },
    { signal },
  );
  const text = await res.text();
  if (!res.ok) throw toIntakeError(res.status, text);
  let data: T;
  try {
    data = (text ? JSON.parse(text) : undefined) as T;
  } catch {
    // A 2xx reply that is not JSON (a proxy page, a truncated body) is still a failed call for the screens.
    throw new IntakeApiError(res.status, 'invalid_response', null);
  }
  return { data, etag: res.headers.get('ETag') };
}

// ---- Status and inbox -------------------------------------------------------------------------------------------

export const getBatch = (patientId: string, batchId: string, signal?: AbortSignal) =>
  call(() => get<IntakeBatch>(`${intakeBase(patientId)}/Batches/${seg(batchId)}`, { signal }));

export const getRunStatus = (patientId: string, runId: string, signal?: AbortSignal) =>
  call(() => get<IntakeRunStatus>(runBase(patientId, runId), { signal }));

export const retryRun = (patientId: string, runId: string) =>
  call(() => post<IntakeRunStatus>(`${runBase(patientId, runId)}/Retry`, {}));

export const getInbox = (patientId: string, status: 'open' | 'all', signal?: AbortSignal) =>
  call(() =>
    get<{ documents: InboxDocument[] }>(`${intakeBase(patientId)}/Inbox?status=${status}`, { signal }),
  );

/** Files a non-lab document to PatientDocument, or a photo to Media. A lab report opens review instead. */
export const addInboxDocument = (patientId: string, documentId: string, docType?: IntakeDocType) =>
  call(() =>
    post<InboxAddResult>(`${intakeBase(patientId)}/Inbox/${seg(documentId)}/Add`, docType ? { docType } : {}),
  );

/** Soft delete; the file stays in the audit record. */
export const disregardInboxDocument = (
  patientId: string,
  documentId: string,
  reason: InboxDisregardReason,
  note: string | null = null,
) => call(() => post<InboxDisregardResult>(`${intakeBase(patientId)}/Inbox/${seg(documentId)}/Disregard`, { reason, note }));

/** The original file, for the source viewer. Fetch it through the client, never by a raw URL (auth + CORS). */
export const documentContentPath = (patientId: string, documentId: string) =>
  `${intakeBase(patientId)}/Documents/${seg(documentId)}/Content`;

// ---- Review and row decisions -----------------------------------------------------------------------------------

export const getReview = (patientId: string, runId: string, signal?: AbortSignal) =>
  versioned<IntakeReview>(`${runBase(patientId, runId)}/Review`, { method: 'GET' }, signal);

const rowPath = (patientId: string, runId: string, rowId: string) =>
  `${runBase(patientId, runId)}/Rows/${seg(rowId)}`;

export const acceptRow = (patientId: string, runId: string, rowId: string, approvedVersion: number, etag: string | null) =>
  versioned<ReviewRow>(`${rowPath(patientId, runId, rowId)}/Accept`, {
    method: 'POST',
    body: { approvedVersion },
    ifMatch: etag,
  });

export const editRow = (patientId: string, runId: string, rowId: string, body: RowEditBody, etag: string | null) =>
  versioned<ReviewRow>(rowPath(patientId, runId, rowId), { method: 'PATCH', body, ifMatch: etag });

export const disregardRow = (
  patientId: string,
  runId: string,
  rowId: string,
  approvedVersion: number,
  reason: RowDisregardReason,
  etag: string | null,
) =>
  versioned<ReviewRow>(`${rowPath(patientId, runId, rowId)}/Disregard`, {
    method: 'POST',
    body: { approvedVersion, reason },
    ifMatch: etag,
  });

/** Manual transcription of a suppressed or missed value; it is saved as Source = Transcribed. */
export const addManualRow = (patientId: string, runId: string, body: ManualRowBody, etag: string | null) =>
  versioned<ReviewRow>(`${runBase(patientId, runId)}/Rows`, { method: 'POST', body, ifMatch: etag });

export const confirmDocument = (patientId: string, runId: string, body: DocumentDecisionBody, etag: string | null) =>
  versioned<IntakeReview['header']['documentDecision']>(`${runBase(patientId, runId)}/Document`, {
    method: 'POST',
    body,
    ifMatch: etag,
  });

/** Discard reading: the run and its staging rows are soft deleted; the file stays attached. */
export const discardRun = (patientId: string, runId: string, reason: string, etag: string | null) =>
  versioned<IntakeRunStatus>(`${runBase(patientId, runId)}/Discard`, { method: 'POST', body: { reason }, ifMatch: etag });

/**
 * Change patient (Patient PR #1168): the document was attached to the wrong patient. It is filed again for
 * `targetPatientId` as a NEW document, batch and run, and read again there; the old document is disregarded as
 * `not_this_patient`. `If-Match` carries the run ETag; there is no Idempotency-Key.
 */
export const changePatient = (patientId: string, runId: string, targetPatientId: string, etag: string | null) =>
  versioned<ChangePatientResult>(`${runBase(patientId, runId)}/ChangePatient`, {
    method: 'POST',
    body: { targetPatientId } satisfies ChangePatientBody,
    ifMatch: etag,
  });

/** Writes the LabReport with the user's own token. Retrying with the same key returns the same result. */
export const confirmRun = (
  patientId: string,
  runId: string,
  body: ConfirmBody,
  etag: string | null,
  idempotencyKey: string,
) =>
  versioned<ConfirmResult>(`${runBase(patientId, runId)}/Confirm`, {
    method: 'POST',
    body,
    ifMatch: etag,
    idempotencyKey,
  });

// ---- Lab component catalogue (v0.6 §8b) -------------------------------------------------------------------------

/** `q` needs at least 2 characters (else 400 `q_too_short`); 503 `store_unavailable` until the catalogue is seeded. */
export const searchLabComponents = (q: string, limit = 20, signal?: AbortSignal) =>
  call(() =>
    get<LabComponentSearch>(`/api/LabComponents?${new URLSearchParams({ q, limit: String(limit) }).toString()}`, { signal }),
  );
